import { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WorkIcon from "@mui/icons-material/Work";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TagIcon from "@mui/icons-material/Tag";
import PersonIcon from "@mui/icons-material/Person";
import "../../styles/Listing/listing-premium.css";
import { useNavigate } from "react-router-dom";
import { deleteRequest, postRequest } from "../../api/apiServices";
import { deleteJob, jobsList } from "../../api/apiList";
import Loading from "../../utils/Loading";
import PaginationComponent from "../../utils/PaginationComponent";
import debounce from "lodash/debounce";
import Toast from "../../utils/Toast";
import ConfirmationDialog from "../../components/DailogBox";
import { roleBasedRedirection } from "../../utils/helper";
import { useAuth } from "../../hooks/useAuth";

interface HiringManager {
  id: number;
  name: string;
  email: string;
}

interface JobVacancyText {
  id: number;
  jobSummariesId: number;
}

interface Jobs {
  id: number;
  jobTitle: string;
  hiringManager: HiringManager;
  jobVacancyText: JobVacancyText;
  workingLocation: string;
  reference_number: string;
}

const JobListing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [job, setJob] = useState<Jobs[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loader, setLoader] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateCampaign = (id: number) => {
    navigate(`/campaigns/create/${id}`);
  };

  const handleUpdate = (id: number) => {
    navigate(roleBasedRedirection(`/jobs/update-job/${id}`));
  };

  const viewAnalysis = (jobTitle: string) => {
    navigate(roleBasedRedirection("/analytics"), { state: { jobTitle } });
  };

  const viewVacancyText = (id: number) => {
    navigate(roleBasedRedirection(`/jobs/vacancy-text/${id}`));
  };

  const handleDelete = async () => {
    if (jobToDelete !== null) {
      const respose = await deleteRequest(deleteJob(jobToDelete));
      Toast(respose?.data?.statusMessage || "Job deleted successfully!");
      jobList(page + 1, rowsPerPage, searchTerm);
      setOpenDialog(false);
      setJobToDelete(null);
    }
  };

  const handleDeleteClick = (id: number) => {
    setJobToDelete(id);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setJobToDelete(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setSearchLoading(true);
    debouncedSearch(value);
  };

  const handleClick = () =>
    navigate(roleBasedRedirection("/jobs/create-jobprofile"));

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      jobList(1, rowsPerPage, searchValue);
      setSearchLoading(false);
    }, 500),
    []
  );

  const jobList = async (page: number, limit: number, search: string) => {
    setLoader(true);
    const response = await postRequest(jobsList, {
      page,
      limit,
      search,
      searchColumns: ["jobTitle", "workingLocation", "reference_number"],
    });
    if (response?.data?.success) {
      setJob(response?.data?.data?.jobs);
      setCount(response?.data?.data?.count);
      setPage(response?.data?.data?.page - 1);
      setHasNextPage(response?.data?.data?.hasNextPage);
      setHasPreviousPage(response?.data?.data?.hasPreviousPage);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (!searchLoading) {
      jobList(page + 1, rowsPerPage, searchTerm);
    }
  }, [page, rowsPerPage]);

  return (
    <>
      <Box className="listing-page">
        {/* Hero */}
        <Box className="listing-hero">
          <Box className="listing-hero-inner">
            <Box>
              <Box className="listing-hero-badge">
                <WorkIcon sx={{ fontSize: 14 }} />
                Jobs
              </Box>
              <h1 className="listing-hero-title">Jobs Management</h1>
              <p className="listing-hero-subtitle">
                Create and manage all your job positions
              </p>
            </Box>

            <Box className="listing-hero-actions">
              <button
                type="button"
                className="listing-hero-btn"
                onClick={handleClick}
              >
                <AddIcon sx={{ fontSize: 18 }} />
                Create New Job
              </button>

              <Box className="listing-hero-search">
                <SearchIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        {loader ? (
          <Loading />
        ) : job && job.length > 0 ? (
          <>
            <Box className="listing-list">
              {job.map((jobItem, index) => (
                <Box key={jobItem.id} className="listing-card">
                  <Box className="listing-card-body">
                    <Box className="listing-card-info">
                      <Box className="listing-card-title-row">
                        <Box className="listing-card-index">
                          {index + 1 + page * rowsPerPage}
                        </Box>
                        <Box>
                          <p className="listing-card-title">
                            {jobItem?.jobTitle}
                          </p>
                          {user?.status !== 1 &&
                            jobItem?.hiringManager?.name && (
                              <Box className="listing-card-subtitle">
                                <PersonIcon sx={{ fontSize: 15, color: "#56c6a9" }} />
                                {jobItem?.hiringManager?.name}
                              </Box>
                            )}
                        </Box>
                      </Box>

                      <Box className="listing-meta-row">
                        {jobItem?.workingLocation && (
                          <span className="listing-meta-chip">
                            <LocationOnIcon />
                            {jobItem.workingLocation.split(",")[0]}
                          </span>
                        )}
                        {jobItem?.reference_number && (
                          <span className="listing-meta-chip">
                            <TagIcon />
                            {jobItem.reference_number}
                          </span>
                        )}
                      </Box>
                    </Box>

                    <Box className="listing-actions">
                      <IconButton
                        className="listing-action-btn"
                        onClick={() => handleCreateCampaign(jobItem?.id)}
                        title="Create Campaign"
                      >
                        <CampaignIcon />
                      </IconButton>
                      {jobItem?.jobVacancyText?.id && (
                        <IconButton
                          className="listing-action-btn"
                          onClick={() => viewVacancyText(jobItem?.id)}
                          title="Vacancy Text"
                        >
                          <DescriptionIcon />
                        </IconButton>
                      )}
                      <IconButton
                        className="listing-action-btn"
                        onClick={() => viewAnalysis(jobItem?.jobTitle)}
                        title="View Analysis"
                      >
                        <BarChartIcon />
                      </IconButton>
                      <IconButton
                        className="listing-action-btn"
                        onClick={() => handleUpdate(jobItem?.id)}
                        title="Edit"
                      >
                        <EditOutlinedIcon />
                      </IconButton>
                      <IconButton
                        className="listing-action-btn listing-action-btn--danger"
                        onClick={() => handleDeleteClick(jobItem?.id)}
                        title="Delete"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box className="listing-pagination-wrap">
              <PaginationComponent
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                count={count}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
              />
            </Box>
          </>
        ) : (
          <Box className="listing-empty">
            <Box className="listing-empty-icon">
              <WorkIcon />
            </Box>
            <h2 className="listing-empty-title">No Jobs Found</h2>
            <p className="listing-empty-text">
              {searchTerm
                ? `No jobs found matching "${searchTerm}". Try adjusting your search.`
                : "Create your first job position to get started with recruitment."}
            </p>
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        open={openDialog}
        onConfirm={handleDelete}
        onCancel={handleClose}
        title="Confirm Deletion"
        message="Are you sure you want to permanently delete this job?"
        cancelButtonName="Cancel"
        confirmButtonName="Delete"
      />
    </>
  );
};

export default JobListing;
